import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ExportedResourceDetail, Migration } from '../../../types';
import { getStatusLabel } from '../../../utils/migrationStatus';
import { getManualStepsWarnings, getTargetReadinessWarnings } from '../../../utils/migrationWarnings';
import { SnapshotHistoryTable } from '../Snapshot/SnapshotHistoryTable';
import { MigrationHistoryTable } from '../Migration/MigrationHistoryTable';
import RetentionEditor from '../Snapshot/RetentionEditor';
import { useToast } from '../../Toast/ToastProvider';
import getConfigSnapshot from '../../../services/Migration/getConfigSnapshotService';
import { ui } from '../../../config/uiSizes';
import { Tooltip } from '../../Tooltip';
import { useAuth } from '../../../hooks/auth';
import { useSnapshotGuidanceSeen } from '../../../hooks/ui/useSnapshotGuidanceSeen';
import { downloadHistoricalSnapshot } from '../../../services/Snapshot/snapshotHistoryService';
import {
  downloadMigrationResults,
  NoResultsAvailableError
} from '../../../services/Migration/downloadMigrationResultsService';
import { useFetchExportedResources } from '../../../hooks/api';
import { getTypeLabelByApiName } from '../../../utils/resourceTypes';
import { formatUtcTimestampLocal, toFriendlyName } from '../../../utils/formatters';
import { getScheduleDisplay, getNextRunDisplay } from '../../../utils/cron';
import { isRollbackEnabled } from '../../../utils/appEnvironment';
import { logger } from '../../../utils/logger';

import Spinner from '../../migratorDashboard/Spinner';
import { RollbackProgressStepper } from '../../ProgressTracker/RollbackProgressStepper';
import NotFullyMigratedBanner from '../../Migrations/NotFullyMigratedBanner';
import TargetReadinessPanel from '../../Migrations/TargetReadinessPanel';
import CollapsibleFindingsSection from '../../Migrations/CollapsibleFindingsSection';
import MigrationPreviewPanel from '../../Migrations/MigrationPreviewPanel';
import RunRealImportButton from '../../Migrations/RunRealImportButton';
import RunDryRunAgainButton from '../../Migrations/RunDryRunAgainButton';
import JobProgressBar from '../../Migrations/JobProgressBar';
import SnapshotIncompleteBanner from '../../Migrations/SnapshotIncompleteBanner';
import { buildMigrationProgressSteps } from '../../../utils/migrationProgressSteps';
import { isReconciliationWarning } from '../../../utils/migrationWarnings';
interface ViewModalContentProps {
  job: Migration;
  onClose?: () => void;
  onSyncNow?: () => void;
  syncNowLoading?: boolean;
}

interface DisplayResourceEntry {
  key: string;
  primary: string;
  secondary?: string;
}

export const ViewModalContent: React.FC<ViewModalContentProps> = ({ job, onClose: _onClose, onSyncNow, syncNowLoading = false }) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { accessToken, userInfo } = useAuth();
  const [downloadingLatest, setDownloadingLatest] = useState(false);
  const [downloadingMigration, setDownloadingMigration] = useState(false);
  const [copyingConfig, setCopyingConfig] = useState(false);
  // Per-user first-time guidance tracking.
  // hasSeen = read from localStorage on mount (determines this session's initial state).
  // showBanner = local session state, starts true if this is their first view.
  const { hasSeen, markAsSeen } = useSnapshotGuidanceSeen(userInfo?.userEmail ?? null);
  const [showBanner, setShowBanner] = useState(!hasSeen);

  // On first view, immediately write to localStorage so next login it won't show again.
  // The banner still stays visible this session until the user clicks X.
  useEffect(() => {
    if (job.isSnapshot && !hasSeen) {
      markAsSeen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.isSnapshot]);

  // Snapshot history is already present on the job when the modal opens —
  // no API call needed.
  const snapshotHistory = useMemo(() => job.snapshotHistory ?? [], [job.snapshotHistory]);

  // Live retention limit — seeded from the job, updated locally after a successful
  // PATCH so the editor reflects the new value without re-opening the modal.
  const [retentionLimit, setRetentionLimit] = useState<number>(job.snapshotRetentionLimit ?? 10);

  // Fetch exported resources for this snapshot
  const {
    exportedResources,
    loading: loadingResources,
    error: exportedResourcesError
  } = useFetchExportedResources(accessToken, job.id);

  const scheduleDisplay = getScheduleDisplay(job.snapshotCronExpression);
  const nextRunDisplay = getNextRunDisplay(job.snapshotCronExpression);
  const typeLabel = job.isSnapshot ? 'Snapshot' : 'Migration';

  // Warnings shown under "Manual steps required" for completed non-snapshot migrations — every
  // completion warning that isn't a pure division-remapping warning.
  const completionWarnings = useMemo(() => getManualStepsWarnings(job), [job]);
  const readinessWarnings = useMemo(() => getTargetReadinessWarnings(job), [job]);
  const isRollbackStatus =
    typeof job.status === 'string' && job.status.startsWith('Rollback');
  // Feature flag — rollback UI. Now generally available (GA) across all environments;
  // governed by the single isRollbackEnabled() helper so it can be re-gated centrally.
  // A dry-run (preview) applied nothing, so there is nothing to roll back — hide rollback for it.
  const isDryRunPreview = !job.isSnapshot && job.job3Import?.dryRun === true;
  const rollbackEnabled = isRollbackEnabled() && !isDryRunPreview;

  // Handler for downloading migration results
  const handleDownloadMigration = async () => {
    if (!accessToken) return;
    try {
      setDownloadingMigration(true);
      await downloadMigrationResults(accessToken, job.id);
      showToast('Download started successfully', 'success');
    } catch (error) {
      if (error instanceof NoResultsAvailableError) {
        showToast('No results are available for this migration', 'warning');
      } else {
        showToast('Failed to download migration results', 'error');
      }
    } finally {
      setDownloadingMigration(false);
    }
  };

  const showMigrationDownloadButton = !job.isSnapshot && job.status.toString() === 'completed';

  // Hidden pending approval — flip to true to re-expose the "Copy Recreate-in-Dev Config" button.
  // The underlying handler and service are intentionally left intact so re-enabling is one line.
  const SHOW_RECREATE_IN_DEV_CONFIG = false;

  // Recreate-in-dev: copy the migration's redacted config as a ready-to-POST CreateMigrationRequest,
  // so support can reproduce this run in another environment without hand-rebuilding the selections.
  const handleCopyConfig = async () => {
    if (!accessToken || copyingConfig) return;
    try {
      setCopyingConfig(true);
      const json = await getConfigSnapshot(accessToken, job.id);
      await navigator.clipboard.writeText(json);
      showToast('Recreate-in-dev config copied to clipboard', 'success');
    } catch {
      showToast('Failed to copy the migration config', 'error');
    } finally {
      setCopyingConfig(false);
    }
  };

  // End-to-end import reconciliation ([RECONCILIATION]) warnings — surfaced so a migration that did
  // not fully land is never presented as complete in the view modal.
  const reconciliationWarnings = useMemo(
    () => (isDryRunPreview ? [] : (job.job3Import?.warnings ?? []).filter(isReconciliationWarning)),
    [job.job3Import?.warnings, isDryRunPreview]
  );

  // Get latest successful snapshot for download button
  const latestSuccessfulSnapshot = useMemo(() => {
    if (!snapshotHistory || snapshotHistory.length === 0) return null;

    // Sort by date descending and find first completed snapshot
    const sorted = [...snapshotHistory].sort(
      (a, b) => new Date(b.capturedAtUtc).getTime() - new Date(a.capturedAtUtc).getTime()
    );
    return sorted.find((entry) => entry.status?.toLowerCase() === 'complete');
  }, [snapshotHistory]);

  // Handler for downloading the latest snapshot
  const handleDownloadLatest = async () => {
    if (!latestSuccessfulSnapshot || !accessToken) return;

    try {
      setDownloadingLatest(true);

      // The timestamp is always the last segment of the blobPath (source of truth)
      const timestamp = latestSuccessfulSnapshot.blobPath.split('/').pop() ?? '';

      await downloadHistoricalSnapshot(accessToken, job.id, timestamp);
      showToast('Snapshot downloaded successfully', 'success');
    } catch (error) {
      logger.error('Error downloading latest snapshot', error instanceof Error ? error : new Error(String(error)));
      showToast('Failed to download snapshot', 'error');
    } finally {
      setDownloadingLatest(false);
    }
  };

  // Process exported resources for display
  const resourcesByType = useMemo(() => {
    if (!exportedResources?.resourceTypes) return new Map<string, DisplayResourceEntry[]>();

    const map = new Map<string, DisplayResourceEntry[]>();

    const getFallbackEntries = (names: string[]): DisplayResourceEntry[] =>
      names.map((name, idx) => ({
        key: `${name}-${idx}`,
        primary: name
      }));

    Object.entries(exportedResources.resourceTypes).forEach(([type, names]) => {
      const detailsForType = exportedResources.resourceDetails?.[type];

      if (Array.isArray(detailsForType) && detailsForType.length > 0) {
        const detailEntries = detailsForType.map((detail: ExportedResourceDetail, idx: number) => {
          if (type === 'genesyscloud_group_roles') {
            const groupName = detail.groupName || detail.key || detail.displayName;
            const roleNames = detail.roleNames || [];
            return {
              key: `${detail.key || groupName}-${idx}`,
              primary: groupName,
              secondary:
                roleNames.length > 0
                  ? `Roles: ${roleNames.join(', ')}`
                  : 'Roles: none assigned'
            };
          }

          return {
            key: `${detail.key || detail.displayName}-${idx}`,
            primary: detail.displayName || detail.key
          };
        });

        map.set(type, detailEntries);
        return;
      }

      map.set(type, getFallbackEntries(names));
    });
    return map;
  }, [exportedResources]);

  const showDownloadButton =
    job.isSnapshot &&
    latestSuccessfulSnapshot &&
    (job.status.toString() === 'SnapshotCompleted' || job.status.toString() === 'SnapshotComplete');

  // --- Status Progress Tracker ---
  type StepStatus = 'completed' | 'current' | 'failed' | 'approval' | 'pending';

  interface ProgressStep {
    label: string;
    subLabel?: string | undefined;
    description: string;
    status: StepStatus;
  }

  const getMigrationSteps = (): ProgressStep[] =>
    buildMigrationProgressSteps({
      status: job.status.toString(),
      transformationsSkipped: job.job2Transform?.status === 'Skipped',
      isDryRunPreview
    });


  const getSnapshotSteps = (): ProgressStep[] => {
    const incomingStatus = job.status.toString();

    const exportSubLabel =
      incomingStatus === 'ExportQueued'
        ? 'Queued'
        : incomingStatus === 'ExportInProgress'
          ? 'In Progress'
          : undefined;

    const snapshotExportStatus: StepStatus =
      incomingStatus === 'ExportFailed'
        ? 'failed'
        : ['ExportQueued', 'ExportInProgress'].includes(incomingStatus)
          ? 'current'
          : incomingStatus === 'SnapshotCompleted'
            ? 'completed'
            : 'pending';

    const completeStatus: StepStatus =
      incomingStatus === 'SnapshotCompleted' ? 'completed' : 'pending';

    return [
      {
        label: 'Export',
        subLabel: exportSubLabel,
        description:
          snapshotExportStatus === 'current'
            ? 'Snapshot is currently capturing resources'
            : snapshotExportStatus === 'failed'
              ? 'Snapshot encountered errors'
              : snapshotExportStatus === 'completed'
                ? 'Resources captured from the source organization'
                : 'Resources will be captured from the source organization',
        status: snapshotExportStatus
      },
      {
        label: 'Complete',
        description:
          completeStatus === 'completed'
            ? 'Snapshot completed successfully'
            : 'Snapshot will be marked complete once all resources are captured',
        status: completeStatus
      }
    ];
  };

  const progressSteps = job.isSnapshot ? getSnapshotSteps() : getMigrationSteps();

  const stepBg: Record<StepStatus, string> = {
    completed: 'bg-green-500 dark:bg-green-600',
    current: 'bg-blue-500 dark:bg-blue-600',
    failed: 'bg-red-500 dark:bg-red-600',
    approval: 'bg-orange-500 dark:bg-orange-600',
    pending: 'bg-gray-300 dark:bg-gray-600'
  };

  const stepIcon = (status: StepStatus, index: number) => {
    if (status === 'completed')
      return (
        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"
            clipRule="evenodd"
          />
        </svg>
      );
    if (status === 'failed')
      return (
        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      );
    if (status === 'approval')
      return (
        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
            clipRule="evenodd"
          />
        </svg>
      );
    if (status === 'current')
      return <span className="w-2 h-2 rounded-full bg-white animate-pulse block" />;
    return <span className="text-xs font-bold text-gray-500 dark:text-gray-400">{index + 1}</span>;
  };

  return (
    <div className="space-y-4">
      {/* Snapshot guidance banner — first-time view only.
          markAsSeen() is called on mount so next login hasSeen=true (tooltip shown).
          X only hides the banner for this session via showBanner. */}
      {job.isSnapshot && showBanner && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-4">
          <div className={`flex items-start justify-between ${ui.gap.md}`}>
            <div className={`flex items-start ${ui.gap.md}`}>
              {/* Info icon */}
              <div className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-amber-400 dark:bg-amber-600 flex items-center justify-center">
                <svg
                  className="w-3 h-3 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth={3}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v.01M12 12v6" />
                </svg>
              </div>
              <div className={`${ui.text.label} text-amber-800 dark:text-amber-200 space-y-2`}>
                <p className={`${ui.text.body} font-semibold text-amber-900 dark:text-amber-100`}>
                  About snapshots
                </p>
                <div className="space-y-1.5">
                  <div className="flex items-start gap-2">
                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 dark:bg-amber-500 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-amber-900 dark:text-amber-100">
                        What a snapshot is —{' '}
                      </span>
                      A point-in-time capture of your Genesys Cloud objects and resources.
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 dark:bg-amber-500 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-amber-900 dark:text-amber-100">
                        Where snapshot files are stored —{' '}
                      </span>
                      In RepliCloud, accessible from this dashboard. Download them at any time.
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-400 dark:bg-amber-500 flex-shrink-0" />
                    <div>
                      <span className="font-semibold text-amber-900 dark:text-amber-100">
                        Who uses snapshots (admins/developers) —{' '}
                      </span>
                      Genesys Cloud administrators and developers use snapshots to review
                      environment resources, check dependencies, and reference settings for manual
                      restore.
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Dismiss button */}
            <button
              type="button"
              onClick={() => setShowBanner(false)}
              title="Dismiss"
              className="flex-shrink-0 text-amber-500 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-200 transition-colors"
              aria-label="Dismiss snapshot guidance"
            >
              <svg
                className={ui.svg.sm}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Name Block */}
      <div className={`p-4 rounded-lg shadow-md bg-white dark:bg-gray-800 border-l-8 ${(!isDryRunPreview && completionWarnings.length > 0) ? 'border-yellow-500 dark:border-yellow-400' : 'border-blue-500 dark:border-blue-400'} border border-gray-200 dark:border-gray-700`}>
        <div className={`flex items-start justify-between ${ui.gap.lg}`}>
          <div className="flex-1">
            <div className={`${ui.text.xs} font-bold uppercase tracking-wider mb-1 ${(!isDryRunPreview && completionWarnings.length > 0) ? 'text-yellow-600 dark:text-yellow-400' : 'text-blue-600 dark:text-blue-400'}`}>
              {typeLabel} Name
            </div>
            <div className={`${ui.text.sectionTitle} font-bold break-words text-gray-900 dark:text-gray-100`}>
              {job.name}
            </div>
          </div>
          {isDryRunPreview ? (
            <div className={`flex items-center gap-1 ${ui.input.modal} bg-indigo-100 dark:bg-indigo-900/30 rounded-lg border border-indigo-200 dark:border-indigo-700 flex-shrink-0`}>
              <svg className={`${ui.svg.sm} text-indigo-600 dark:text-indigo-400`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span className={`${ui.text.badge} font-semibold text-indigo-700 dark:text-indigo-300`}>Preview</span>
            </div>
          ) : completionWarnings.length > 0 ? (
            <div className={`flex items-center gap-1 ${ui.input.modal} bg-yellow-100 dark:bg-yellow-900/30 rounded-lg border border-yellow-200 dark:border-yellow-700 flex-shrink-0`}>
              <svg className={`${ui.svg.sm} text-yellow-600 dark:text-yellow-400`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span className={`${ui.text.badge} font-semibold text-yellow-700 dark:text-yellow-300`}>Warning</span>
            </div>
          ) : (
            <div className={`flex items-center gap-1 ${ui.input.modal} bg-green-100 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-700 flex-shrink-0`}>
              <svg className={`${ui.svg.sm} text-green-600 dark:text-green-400`} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className={`${ui.text.badge} font-semibold text-green-700 dark:text-green-300`}>{getStatusLabel(job.status.toString())}</span>
            </div>
          )}
        </div>
      </div>

      {/* Dry-run preview — no changes were applied to the target org (preview only). Rollback is
          hidden for this state; the preview panel below shows confidence + remediation work-list. */}
      {isDryRunPreview && (
        <div
          className={`flex items-start ${ui.gap.sm} bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-300 dark:border-indigo-700 rounded-lg ${ui.page.sectionPadding} ${ui.mb.title}`}
          role="status"
        >
          <svg
            className={`${ui.svg.sm} text-indigo-600 dark:text-indigo-400 shrink-0 mt-0.5`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          <div className="min-w-0">
            <p className={`${ui.text.label} font-bold text-indigo-800 dark:text-indigo-200`}>
              Dry-run preview completed
            </p>
            <p className={`${ui.text.xs} text-indigo-700 dark:text-indigo-300 mt-0.5 break-words`}>
              No changes were applied to the target org — this was a preview only. See the migration
              preview below for the confidence score and remediation work-list.
            </p>
          </div>
        </div>
      )}

      {/* Target-org readiness prerequisites — TTS voices, duplicate names, division cap,
          emergency-number collisions, and deprecated flow types the operator must resolve in the
          target org. Renders nothing when there are no readiness findings. */}
      <TargetReadinessPanel warnings={readinessWarnings} />

      {/* Manual-remediation banner — cascade-excluded resources that need operator action. Suppressed for
          a dry-run preview (nothing was applied, so there are no manual finish steps yet). */}
      {!isDryRunPreview && completionWarnings.length > 0 && (
        <CollapsibleFindingsSection
          variant="yellow"
          title="Manual steps required"
          iconPath="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          count={completionWarnings.length}
          sectionId="manual-steps"
          summary="This migration completed, but the items below may require manual follow-up."
        >
          <p className={`${ui.text.xs} text-yellow-700 dark:text-yellow-300 ${ui.mb.sm}`}>
            This migration completed, but the items below may require manual follow-up.
          </p>
          <ul className={`flex flex-col ${ui.gap.xs}`}>
            {completionWarnings.map((warning, index) => (
              <li key={index} className={`${ui.text.xs} text-yellow-800 dark:text-yellow-200 break-words`}>
                {warning}
              </li>
            ))}
          </ul>
        </CollapsibleFindingsSection>
      )}

      {/* End-to-end reconciliation — surfaces resources that were requested but did not land in the
          target org ([RECONCILIATION] rollup + per-type breakdown from the import job). Migration
          only; renders nothing for snapshots or when everything migrated. */}
      {!job.isSnapshot && <NotFullyMigratedBanner warnings={reconciliationWarnings} />}

      {/* Snapshot completeness — surfaces draft-only flows the platform could not capture
          ([SNAPSHOT-INCOMPLETE] on the export job). Snapshot only; renders nothing when every flow
          with a published version was captured. */}
      {job.isSnapshot && <SnapshotIncompleteBanner warnings={job.job1Export?.warnings} />}

      {/* Dry-run preview — remediation work-list + confidence for a migration run as a dry run.
          Shows every resource that will not cleanly apply with a cause and a concrete fix, and a
          "Re-run preview" control to support the fix → re-preview loop. Migration + dry-run only. */}
      {!job.isSnapshot && job.job3Import?.dryRun === true && accessToken && (
        <>
          <MigrationPreviewPanel token={accessToken} migrationId={job.id} className={ui.mb.title} />
          <div className={`flex flex-wrap items-center ${ui.gap.md} ${ui.mb.title}`}>
            <RunDryRunAgainButton token={accessToken} migrationId={job.id} onQueued={_onClose} />
            <RunRealImportButton token={accessToken} migrationId={job.id} onImportQueued={_onClose} />
          </div>
        </>
      )}

      {/* Download Migration Results Button */}
      {showMigrationDownloadButton && (
        <div className="relative group/download">
          <button
            onClick={handleDownloadMigration}
            disabled={downloadingMigration}
            className={`w-full flex items-center justify-center ${ui.gap.md} ${ui.button.lg} bg-purple-600 hover:bg-purple-700 dark:bg-purple-700 dark:hover:bg-purple-800 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-lg hover:shadow-xl`}
          >
            {downloadingMigration ? (
              <>
                <svg className={`${ui.svg.md} animate-spin`} fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span className={`${ui.text.body}`}>Downloading Migration Results...</span>
              </>
            ) : (
              <>
                <svg className={`${ui.svg.md}`} fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className={`${ui.text.body}`}>Download Migration Results (Optional)</span>
              </>
            )}
          </button>
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/download:block z-50 pointer-events-none w-64">
            <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg px-3 py-2 shadow-lg text-center leading-relaxed">
              Optional: download migration results for offline review. This does not affect your
              migration status.
            </div>
          </div>
        </div>
      )}

      {/* Copy recreate-in-dev config — migration only. Copies the redacted CreateMigrationRequest JSON.
          Hidden pending approval via SHOW_RECREATE_IN_DEV_CONFIG. */}
      {SHOW_RECREATE_IN_DEV_CONFIG && !job.isSnapshot && (
        <div className="relative group/config">
          <button
            type="button"
            onClick={handleCopyConfig}
            disabled={copyingConfig}
            className={`w-full flex items-center justify-center ${ui.gap.md} ${ui.button.lg} bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold`}
          >
            {copyingConfig ? (
              <>
                <svg className={`${ui.svg.md} animate-spin`} fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className={`${ui.text.body}`}>Copying config…</span>
              </>
            ) : (
              <>
                <svg className={`${ui.svg.md}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span className={`${ui.text.body}`}>Copy Recreate-in-Dev Config</span>
              </>
            )}
          </button>
          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/config:block z-50 pointer-events-none w-64">
            <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg px-3 py-2 shadow-lg text-center leading-relaxed">
              Copies this migration&apos;s configuration as a ready-to-POST request (credentials
              excluded) so support can reproduce it in another environment.
            </div>
          </div>
        </div>
      )}

      {/* View Change Details Button — migration only, completed status */}
      {!job.isSnapshot && job.status.toString() === 'completed' && (
        <button
          type="button"
          onClick={() => navigate('/SnapshotComparisonTool', { state: { job } })}
          className={`w-full flex items-center justify-center ${ui.gap.md} ${ui.button.lg} bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white rounded-xl transition-colors font-semibold shadow-lg hover:shadow-xl`}
        >
          <svg
            className={ui.svg.md}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <span className={ui.text.body}>Review Target Organization Changes</span>
        </button>
      )}

      {/* Download Latest Snapshot Button */}
      {showDownloadButton && (
        <button
          onClick={handleDownloadLatest}
          disabled={downloadingLatest}
          className={`w-full flex items-center justify-center ${ui.gap.md} ${ui.button.lg} bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-lg hover:shadow-xl`}
        >
          {downloadingLatest ? (
            <>
              <svg className={`${ui.svg.md} animate-spin`} fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className={`${ui.text.body}`}>Downloading Latest Snapshot...</span>
            </>
          ) : (
            <>
              <svg className={`${ui.svg.md}`} fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              <span className={`${ui.text.body}`}>Download Latest Snapshot</span>
            </>
          )}
        </button>
      )}

      {/* Schedule Display */}
      {scheduleDisplay && (
        <div
          className={`flex items-center ${ui.gap.lg} ${ui.page.sectionPadding} bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700`}
        >
          <div className="p-2 bg-blue-100 dark:bg-blue-800/50 rounded-lg">
            <svg
              className={`${ui.svg.sm} text-blue-600 dark:text-blue-400`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div>
            <div className={`${ui.text.label} font-medium text-blue-700 dark:text-blue-300 mb-1`}>
              Recurring Schedule
            </div>
            <div className={`${ui.text.label} font-bold text-blue-900 dark:text-blue-100`}>
              {scheduleDisplay}
            </div>
            {nextRunDisplay && (
              <div className={`${ui.text.xs} text-blue-700 dark:text-blue-300 mt-1`}>
                Next run: {nextRunDisplay}
              </div>
            )}
          </div>
        </div>
      )}

      {rollbackEnabled && isRollbackStatus ? (
        <RollbackProgressStepper status={job.status.toString()} />
      ) : (
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div
            className={`${ui.text.label} font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3`}
          >
            {typeLabel} Progress
          </div>
          <div className="relative flex items-start gap-0">
            {progressSteps.map((step, index) => (
              <div key={step.label} className="flex-1 flex flex-col items-center relative">
                {/* Connector line */}
                {index < progressSteps.length - 1 && (
                  <div
                    className={`absolute top-3.5 left-1/2 w-full h-0.5 z-0 ${step.status === 'completed'
                      ? 'bg-green-400 dark:bg-green-600'
                      : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                  />
                )}

                {/* Circle */}
                <div
                  className={`relative z-10 w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${stepBg[step.status]} ${step.status === 'current'
                    ? 'ring-2 ring-offset-2 ring-blue-400 dark:ring-offset-gray-800'
                    : ''
                    } ${step.status === 'approval'
                      ? 'ring-2 ring-offset-2 ring-orange-300 dark:ring-offset-gray-800'
                      : ''
                    }`}
                >
                  {stepIcon(step.status, index)}
                </div>

                {/* Label + description */}
                <div className="mt-2 text-center px-1">
                  <div className="flex items-center justify-center gap-1 flex-wrap">
                    <div
                      className={`font-semibold ${ui.text.label} ${step.status === 'completed'
                        ? 'text-green-700 dark:text-green-400'
                        : step.status === 'current'
                          ? 'text-blue-700 dark:text-blue-300'
                          : step.status === 'failed'
                            ? 'text-red-600 dark:text-red-400'
                            : step.status === 'approval'
                              ? 'text-orange-600 dark:text-orange-400'
                              : 'text-gray-400 dark:text-gray-500'
                        }`}
                    >
                      {step.label}
                    </div>
                    {step.subLabel && (
                      <span
                        className={`inline-block ${ui.text.label} font-semibold px-1.5 py-0.5 rounded-full leading-none ${step.subLabel === 'Queued'
                          ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'
                          : step.subLabel === 'Partial'
                            ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                          }`}
                      >
                        {step.subLabel}
                      </span>
                    )}
                  </div>
                  <div
                    className={`${ui.text.label} text-gray-500 dark:text-gray-400 mt-0.5 leading-tight`}
                  >
                    {step.description}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Live import progress bar (parsed from job3Import.notes) — shown only for a
          non-snapshot migration while the import is actively reporting progress. */}
      {!job.isSnapshot && <JobProgressBar notes={job.job3Import?.notes} />}

      {/* Details Grid */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <svg
              className={`${ui.svg.sm} text-blue-600 dark:text-blue-400`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
              <path
                fillRule="evenodd"
                d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className={`${ui.text.label} font-medium text-gray-500 dark:text-gray-400 mb-1`}>
              Type
            </div>
            <div className={`font-semibold ${ui.text.label} text-gray-900 dark:text-gray-100`}>
              {typeLabel}
            </div>
          </div>
        </div>

        <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
            <svg
              className={`${ui.svg.sm} text-purple-600 dark:text-purple-400`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className={`${ui.text.label} font-medium text-gray-500 dark:text-gray-400 mb-1`}>
              Created By
            </div>
            <div
              className={`font-semibold ${ui.text.label} text-gray-900 dark:text-gray-100 md:truncate xl:whitespace-normal xl:overflow-visible`}
              title={job.createdBy}
            >
              {job.createdBy}
            </div>
          </div>
        </div>

        <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
            <svg
              className={`${ui.svg.sm} text-orange-600 dark:text-orange-400`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className={`${ui.text.label} font-medium text-gray-500 dark:text-gray-400 mb-1`}>
              Created At
            </div>
            <div className={`font-semibold ${ui.text.label} text-gray-900 dark:text-gray-100`}>
              {formatUtcTimestampLocal(job.createdAtUtc)}
            </div>
          </div>
        </div>

        {job.updatedAtUtc && (
          <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
              <svg
                className={`${ui.svg.sm} text-teal-600 dark:text-teal-400`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div className={`${ui.text.label} font-medium text-gray-500 dark:text-gray-400 mb-1`}>
                Last Updated
              </div>
              <div className={`font-semibold ${ui.text.label} text-gray-900 dark:text-gray-100`}>
                {formatUtcTimestampLocal(job.updatedAtUtc)}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Selected Resources Section */}
      {(job.isSnapshot || !job.isSnapshot) && (
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 mt-6">
          <div
            className={`${ui.text.label} font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3 flex items-center`}
          >
            Selected Resources
            <Tooltip
              position="right"
              content={
                job.isSnapshot
                  ? 'The resources that were selected when this snapshot was configured. This shows what will be captured when the snapshot runs.'
                  : 'The resources that were selected when this migration was configured.'
              }
            />
          </div>

          {loadingResources ? (
            <div className="flex flex-col items-center gap-2 py-6">
              <Spinner />
              <p className={`${ui.text.body} text-gray-500 dark:text-gray-400`}>
                Loading resources...
              </p>
            </div>
          ) : exportedResourcesError ? (
            <div className="py-6 text-center">
              <p className={`${ui.text.body} text-gray-500 dark:text-gray-400`}>
                Failed to load resources
              </p>
              <p className={`${ui.text.xs} text-gray-400 dark:text-gray-500 mt-1`}>
                {exportedResourcesError.message}
              </p>
            </div>
          ) : job.exportAll ? (
            <div
              className={`flex items-center ${ui.gap.md} py-4 px-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700`}
            >
              <svg
                className={`${ui.svg.md} text-blue-600 dark:text-blue-400 flex-shrink-0`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <div className={`${ui.text.body} font-semibold text-blue-900 dark:text-blue-100`}>
                  Full Snapshot
                </div>
                <div className={`${ui.text.xs} text-blue-700 dark:text-blue-300 mt-0.5`}>
                  All supported resource types are included in this snapshot
                </div>
              </div>
            </div>
          ) : resourcesByType.size > 0 ? (
            <div className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden max-h-80 overflow-y-auto">
              {Array.from(resourcesByType.entries()).map(([type, entries]) => (
                <details key={type} className="group bg-white dark:bg-gray-800">
                  <summary
                    className={`flex items-center justify-between ${ui.page.rowPadding} ${ui.gap.md} cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition`}
                  >
                    <div className="flex items-center gap-3">
                      <svg
                        className={`${ui.svg.sm} text-gray-400 transition-transform group-open:rotate-90`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span
                        className={`${ui.text.body} font-semibold text-gray-900 dark:text-gray-100`}
                      >
                        {getTypeLabelByApiName(toFriendlyName(type))}
                      </span>
                    </div>
                    <span
                      className={`${ui.text.xs} font-bold text-white bg-blue-600 dark:bg-blue-700 ${ui.badge.padding} rounded-full whitespace-nowrap`}
                    >
                      {entries.length}
                    </span>
                  </summary>

                  <div
                    className={`${ui.page.rowPadding} bg-gray-50 dark:bg-gray-900 max-h-48 overflow-y-auto`}
                  >
                    <div className="space-y-1">
                      {entries.map((entry) => (
                        <div
                          key={entry.key}
                          className={`flex items-start gap-2 py-1 min-w-0 ${ui.text.label} text-gray-700 dark:text-gray-300`}
                        >
                          <svg
                            className={`${ui.svg.sm} text-green-500 flex-shrink-0 mt-0.5`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <div className="min-w-0">
                            <div className="break-all">{entry.primary}</div>
                            {entry.secondary && (
                              <div className={`${ui.text.xs} text-gray-500 dark:text-gray-400 mt-0.5 break-all`}>
                                {entry.secondary}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </details>
              ))}
            </div>
          ) : (
            <div className="py-6 text-center text-gray-400 dark:text-gray-500 italic">
              <p className={`${ui.text.body}`}>No resources selected</p>
            </div>
          )}
        </div>
      )}

      {/* View Detailed Logs Button — snapshots (any) or migrations with schedule */}
      {(job.isSnapshot || job.snapshotCronExpression) && (
        <button
          type="button"
          onClick={() => navigate('/MigrationLogs', { state: { job } })}
          className={`mt-6 w-full flex items-center justify-center ${ui.gap.md} ${ui.button.lg} bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white rounded-xl transition-colors font-semibold shadow-lg hover:shadow-xl`}
        >
          <svg className={`${ui.svg.md}`} fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z"
              clipRule="evenodd"
            />
          </svg>
          <span className={ui.text.body}>View Detailed Logs</span>
        </button>
      )}

      {/* Sync Now + History — shown for any recurring job (with snapshotCronExpression) */}
      {job.snapshotCronExpression && (
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 space-y-6">

          {/* Manual Sync — org sync migrations only */}
          {!job.isSnapshot && job.targetOrgId && onSyncNow && (
            <div className={`flex items-center justify-between ${ui.page.sectionPadding} bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-200 dark:border-teal-700`}>
              <div className={`flex items-start ${ui.gap.md}`}>
                <div className="p-2 bg-teal-100 dark:bg-teal-800/50 rounded-lg shrink-0">
                  <svg className={`${ui.svg.sm} text-teal-600 dark:text-teal-400`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <div className={`${ui.text.label} font-semibold text-teal-800 dark:text-teal-200`}>Manual Sync</div>
                  <div className={`${ui.text.xs} text-teal-600 dark:text-teal-400 mt-0.5`}>
                    Trigger a sync run immediately, outside the regular schedule. Useful for the first run or after resolving a failure.
                  </div>
                </div>
              </div>
              <button
                onClick={onSyncNow}
                disabled={syncNowLoading}
                className={`shrink-0 ml-4 rounded-lg bg-teal-600 hover:bg-teal-700 dark:bg-teal-700 dark:hover:bg-teal-600 text-white font-semibold ${ui.button.md} shadow transition flex items-center ${ui.gap.xs} ${syncNowLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
                type="button"
              >
                {syncNowLoading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    <span>Syncing...</span>
                  </>
                ) : (
                  <>
                    <svg className={`${ui.svg.sm}`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                    </svg>
                    <span>Sync Now</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* Retention editor — snapshots only. Lets users adjust how many snapshots
              are kept, wiring the previously API-only PATCH {id}/retention endpoint. */}
          {job.isSnapshot && (
            <RetentionEditor
              migrationId={job.id}
              currentLimit={retentionLimit}
              historyCount={snapshotHistory.length}
              onUpdated={(newLimit) => setRetentionLimit(newLimit)}
            />
          )}

          {/* Snapshot / Migration History */}
          <div>
            <h3 className={`${ui.text.subTitle} font-semibold text-gray-900 dark:text-gray-100 ${ui.mb.title} flex items-center gap-2`}>
              <svg
                className={`${ui.svg.md} text-blue-600 dark:text-blue-400`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                  clipRule="evenodd"
                />
              </svg>
              {job.isSnapshot ? 'Snapshot History' : 'Migration History'}
              <Tooltip
                position="right"
                content={job.isSnapshot
                  ? "A log of all snapshots captured for this migration. Each row represents one scheduled or manual export data. Click Download on any row to download that snapshot's exported results as a ZIP file."
                  : "A log of all migration runs for this recurring migration. Each row represents one scheduled or manual migration execution. Click Download on any row to download that migration's results as a ZIP file."
                }
              />
            </h3>
            {snapshotHistory.length > 0 ? (
              job.isSnapshot ? (
                <SnapshotHistoryTable history={snapshotHistory} migrationId={job.id} />
              ) : (
                <MigrationHistoryTable history={snapshotHistory} migrationId={job.id} />
              )
            ) : (
              <div
                className={`flex items-center justify-center py-6 ${ui.text.label} text-gray-400 dark:text-gray-500 italic`}
              >
                No history available
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};
